import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Repeat, MonitorPlay, CalendarClock, KeyRound,
  LayoutDashboard, 
  Users, 
  Trophy, 
  Calendar, 
  Newspaper, 
  Image, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  UserCircle,
  Shield,
  EyeOff,
  Move,
  AlertTriangle,
  History
} from "lucide-react";
import { useState } from "react";
import { canAccess, ROLE_LABELS } from "@/lib/staffAccess";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_soccer-pro-21/artifacts/6hs5jyaw_image-removebg-preview.png";

const AdminLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { href: "/admin", icon: LayoutDashboard, label: "Dashboard", area: "staff" },
    { href: "/admin/competitions", icon: Trophy, label: "Competitions", area: "data" },
    { href: "/admin/team-assignments", icon: Move, label: "Team Assignments", area: "data" },
    { href: "/admin/schedule-import", icon: CalendarClock, label: "Schedule Import", area: "matches" },
    { href: "/admin/season-rollover", icon: Repeat, label: "Promotion & Relegation" },
    { href: "/big-screen", icon: MonitorPlay, label: "Stadium Big Screen" },
    { href: "/admin/teams", icon: Users, label: "Teams", area: "data" },
    { href: "/admin/players", icon: UserCircle, label: "Players", area: "data" },
    { href: "/admin/matches", icon: Calendar, label: "Matches", area: "matches" },
    { href: "/admin/managers", icon: Shield, label: "Managers" },
    { href: "/admin/news", icon: Newspaper, label: "News", area: "news" },
    { href: "/admin/media", icon: Image, label: "Media", area: "news" },
    { href: "/admin/pages", icon: EyeOff, label: "Pages" },
    { href: "/admin/data-quality", icon: AlertTriangle, label: "Data Quality" },
    { href: "/admin/audit-log", icon: History, label: "Audit Log" },
    { href: "/admin/staff", icon: KeyRound, label: "Staff Logins", area: "super" },
  ].filter((item) => canAccess(user, item.area));

  const isActive = (href) => {
    if (href === "/admin") return location.pathname === "/admin";
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile Header */}
      <div className="lg:hidden bg-[#09090b] text-white px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="ABFA" className="h-10" />
          <span className="font-heading font-bold uppercase">Admin</span>
        </Link>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="admin-mobile-menu-toggle">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside 
          className={`
            fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50 w-64 h-screen flex flex-col bg-[#09090b] text-white transform transition-transform duration-300
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
          data-testid="admin-sidebar"
        >
          {/* Logo */}
          <div className="hidden lg:flex items-center gap-3 px-6 py-6 border-b border-gray-800">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="ABFA" className="h-12" />
              <div>
                <p className="font-heading font-bold uppercase">ABFA Admin</p>
                <p className="text-xs text-gray-500">Management System</p>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="py-6 px-4 flex-1 overflow-y-auto">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded transition-colors
                      ${isActive(item.href) 
                        ? 'bg-[#FFC72C] text-black' 
                        : 'text-gray-400 hover:bg-gray-800 hover:text-white'}
                    `}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`admin-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                    {isActive(item.href) && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-gray-800 shrink-0">
            <div className="flex items-center gap-3 px-4 py-2 mb-2">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                  <UserCircle className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                <p className="text-[10px] uppercase tracking-wider text-[#FFC72C]" data-testid="admin-user-role">{user?.is_super_admin ? "Super Admin" : ROLE_LABELS[user?.role] || user?.role}</p>
              </div>
            </div>
            <ChangePasswordDialog />
            <button
              onClick={logout}
              className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-900/20 rounded transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
            <Link
              to="/"
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-400 hover:bg-gray-800 rounded transition-colors mt-1"
              data-testid="admin-view-site-btn"
            >
              <ChevronRight className="w-5 h-5" />
              <span className="font-medium">View Site</span>
            </Link>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8">
          {title && (
            <div className="mb-8">
              <h1 className="font-heading text-3xl font-bold uppercase text-[#09090b]" data-testid="admin-page-title">
                {title}
              </h1>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
